import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import MethodExecutionLimitError from 'studies/typed/process-development/MethodExecutionLimitError';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import ControlledNumericInput from 'ui-library/inputs/ControlledNumericInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {NumericInputChangeEvent} from 'util/Types';

import ReplicateLimitLabel from './ReplicateLimitLabel';

export interface ConditionsSpec {
  numConditions: number;
  numReplicates: number;
}

interface ConditionsState extends ConditionsSpec {
  validationError?: JSX.Element;
}

export interface AddConditionsProps {
  onRequestClose: () => void;
  onRequestSubmit: (result: ConditionsSpec) => void;
  remainingReplicates: number;
  replicateLimit: number;
  initialReplicates: number;
  procedureType: ProcedureType;
}

export default function AddConditionsModal({
  onRequestClose,
  onRequestSubmit,
  remainingReplicates,
  replicateLimit,
  initialReplicates,
  procedureType,
}: AddConditionsProps): JSX.Element {
  const [conditionsState, setConditionsState] = React.useState<ConditionsState>(
    validateConditionsSpec(
      {
        numConditions: 1,
        numReplicates: initialReplicates,
      },
      procedureType
    )
  );

  function validateConditionsSpec(spec: ConditionsSpec, procedureType: ProcedureType): ConditionsState {
    if (spec.numConditions < 1 || spec.numReplicates < 1) {
      return {
        ...spec,
        validationError: (
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`Number of ${getProcedureRunConditionTextForProcedureType(procedureType, {
              plural: true,
            })} and number of replicates must be at least 1.`}
          />
        ),
      };
    }
    if (spec.numConditions * spec.numReplicates > remainingReplicates) {
      return {
        ...spec,
        validationError: (
          <MethodExecutionLimitError replicateLimit={replicateLimit} procedureType={procedureType} />
        ),
      };
    }
    return spec;
  }

  return (
    <Modal
      buttons={[
        {
          text: `Add ${conditionsState.numConditions} ${pluralize(
            conditionsState.numConditions,
            getProcedureRunConditionTextForProcedureType(procedureType)
          )}`,
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled:
            Boolean(conditionsState.validationError) ||
            conditionsState.numConditions * conditionsState.numReplicates > remainingReplicates,
          dataTestAttrs: {component: 'AddConditionsModal', element: 'addConditionsButton'},
        },
      ]}
      title={`Add multiple ${getProcedureRunConditionTextForProcedureType(procedureType, {plural: true})}`}
      onRequestClose={onRequestClose}
      onRequestSubmit={() => {
        onRequestSubmit({
          numConditions: conditionsState.numConditions,
          numReplicates: conditionsState.numReplicates,
        });
      }}
    >
      <div className={css(styles.container)}>
        <FormGroup>
          <FieldLabel
            label={`Number of ${getProcedureRunConditionTextForProcedureType(procedureType, {
              plural: true,
            })}`}
          />
          <ControlledNumericInput
            value={conditionsState.numConditions}
            onChange={(e: NumericInputChangeEvent) => {
              setConditionsState(
                validateConditionsSpec(
                  {
                    numConditions: e.target.numericValue ?? 0,
                    numReplicates: conditionsState.numReplicates,
                  },
                  procedureType
                )
              );
            }}
            validateInteger={true}
            required={true}
            min={1}
            focusOnMount={true}
            dataTestAttrs={{component: 'AddConditionsModal', element: 'numConditionsInput'}}
          />
        </FormGroup>
        <FormGroup>
          <FieldLabel
            label={`Number of replicates per ${getProcedureRunConditionTextForProcedureType(procedureType)}`}
          />
          <ControlledNumericInput
            value={conditionsState.numReplicates}
            onChange={(e: NumericInputChangeEvent) => {
              setConditionsState(
                validateConditionsSpec(
                  {
                    numConditions: conditionsState.numConditions,
                    numReplicates: e.target.numericValue ?? 0,
                  },
                  procedureType
                )
              );
            }}
            validateInteger={true}
            required={true}
            min={1}
            dataTestAttrs={{component: 'AddConditionsModal', element: 'numReplicatesInput'}}
          />
        </FormGroup>
        <FormGroup disableBottomMargin={!conditionsState.validationError}>
          <ReplicateLimitLabel replicateLimit={replicateLimit} />
        </FormGroup>
        {conditionsState.validationError && (
          <FormGroup disableBottomMargin={true}>{conditionsState.validationError}</FormGroup>
        )}
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    // The designs call for a 24px total padding. The modal already includes 16px of padding.
    padding: 8,
  },
});
